import { useCallback, useEffect, useRef, useState } from 'react';
import { getToken, isAuthenticated as isProAuthenticated } from './auth';
import {
  getCustomerToken,
  isCustomerAuthenticated,
} from './customer-auth';
import { getUnreadMessageCount } from './messaging-api';

/**
 * Real-time messaging via Server-Sent Events. The browser's native
 * EventSource can't set an Authorization header, so we pass the JWT as a
 * `?token=` query param (verified server-side on the SSE route).
 *
 * SSE is the live "nudge" layer; the unread-count + thread APIs remain
 * the source of truth, so a dropped connection / missed event self-heals
 * on the next poll or page load.
 */

type Variant = 'pro' | 'customer';

const BASE_URL =
  (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3002';
const POLL_INTERVAL_MS = 60_000;

// Cross-component signal so the header badge re-fetches its count when a
// thread is opened/marked-read elsewhere (mirrors notifications).
const MESSAGES_CHANGED_EVENT = 'cherry:messages-changed';

/** Tell every mounted message badge to re-fetch the unread count. */
export function notifyMessagesChanged() {
  window.dispatchEvent(new Event(MESSAGES_CHANGED_EVENT));
}

export type IncomingMessage = {
  message_id: string;
  sender_user_id: string;
  booking_id: string | null;
  preview: string;
};

/** A live in-app notification nudge (e.g. a new review). Carries just
 *  enough to toast; the bell re-fetches its count on receipt. */
export type IncomingNotification = {
  kind?: string;
  title?: string;
  body?: string;
};

const tokenFor = (v: Variant) =>
  v === 'pro' ? getToken() : getCustomerToken();
const isAuthedFor = (v: Variant) =>
  v === 'pro' ? isProAuthenticated() : isCustomerAuthenticated();

/**
 * Open ONE SSE stream for the signed-in user and dispatch its events:
 * `onMessage` for new chat messages, and the optional `onNotification`
 * for live in-app notifications (reviews, etc.) so the bell can update
 * instantly instead of waiting for its 60s poll. No-ops when not signed
 * in for `variant`. EventSource reconnects automatically; we stop only
 * after repeated failures (e.g. an expired token 401-looping).
 */
export function useMessageStream(
  variant: Variant,
  onMessage: (m: IncomingMessage) => void,
  onNotification?: (n: IncomingNotification) => void,
) {
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;
  const notifCbRef = useRef(onNotification);
  notifCbRef.current = onNotification;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return;
    }
    if (!isAuthedFor(variant)) return;
    const token = tokenFor(variant);
    if (!token) return;

    const url = `${BASE_URL.replace(/\/$/, '')}/me/messages/stream?token=${encodeURIComponent(
      token,
    )}`;
    const es = new EventSource(url);
    let errorCount = 0;

    es.onopen = () => {
      errorCount = 0;
    };
    const onMsg = (e: MessageEvent) => {
      try {
        cbRef.current(JSON.parse(e.data));
      } catch {
        /* malformed frame — ignore */
      }
    };
    const onNotif = (e: MessageEvent) => {
      try {
        notifCbRef.current?.(JSON.parse(e.data));
      } catch {
        /* malformed frame — ignore */
      }
    };
    es.addEventListener('message', onMsg as EventListener);
    es.addEventListener('notification', onNotif as EventListener);
    es.onerror = () => {
      // EventSource retries on its own; bail out after a burst of
      // failures so a genuinely dead/expired session doesn't loop.
      errorCount += 1;
      if (errorCount >= 5) es.close();
    };

    return () => {
      es.removeEventListener('message', onMsg as EventListener);
      es.removeEventListener('notification', onNotif as EventListener);
      es.close();
    };
  }, [variant]);
}

/**
 * Live unread-message count: initial fetch + 60s poll + refresh on tab
 * focus + instant bump from the SSE stream + cross-component sync.
 */
export function useUnreadMessages(variant: Variant) {
  const [count, setCount] = useState(0);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!isAuthedFor(variant)) {
      if (mountedRef.current) setCount(0);
      return;
    }
    try {
      const res = await getUnreadMessageCount(variant);
      if (mountedRef.current) setCount(res?.count ?? 0);
    } catch {
      /* keep the last good count */
    }
  }, [variant]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const interval = window.setInterval(refresh, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(MESSAGES_CHANGED_EVENT, refresh);
    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(MESSAGES_CHANGED_EVENT, refresh);
    };
  }, [refresh]);

  return { count, refresh };
}
